
import { JavaCourse } from './types';

const JAVA_ADVANCED_MODULES = [
  {
    id: 'java-concurrency',
    title: 'Concurrency and Multithreading',
    description: 'Master concurrent programming with threads, executors, and synchronization.',
    content: `
# Java Concurrency and Multithreading

Concurrent programming allows multiple threads to execute simultaneously, improving application performance.

## Core Concepts

### Thread Basics
\`\`\`java
// Extending Thread class
class MyThread extends Thread {
    public void run() {
        System.out.println("Thread running: " + getName());
    }
}

// Implementing Runnable interface
class MyRunnable implements Runnable {
    public void run() {
        System.out.println("Runnable running: " + Thread.currentThread().getName());
    }
}
\`\`\`

### ExecutorService
\`\`\`java
ExecutorService executor = Executors.newFixedThreadPool(5);

executor.submit(() -> {
    System.out.println("Task executed by: " + Thread.currentThread().getName());
});

executor.shutdown();
\`\`\`

## Synchronization

### Synchronized Methods and Blocks
\`\`\`java
public class Counter {
    private int count = 0;
    
    public synchronized void increment() {
        count++;
    }
    
    public void incrementBlock() {
        synchronized(this) {
            count++;
        }
    }
}
\`\`\`

### Locks and Conditions
\`\`\`java
private final ReentrantLock lock = new ReentrantLock();
private final Condition notEmpty = lock.newCondition();

public void put(Object item) {
    lock.lock();
    try {
        // Add item to queue
        notEmpty.signal();
    } finally {
        lock.unlock();
    }
}
\`\`\`

## Concurrent Collections

- **ConcurrentHashMap**: Thread-safe HashMap
- **BlockingQueue**: Thread-safe queue with blocking operations
- **CopyOnWriteArrayList**: Thread-safe list for read-heavy scenarios

## Best Practices

1. **Avoid shared mutable state** when possible
2. **Use immutable objects** for thread safety
3. **Prefer high-level concurrency utilities** over low-level synchronization
4. **Be aware of deadlocks** and how to prevent them
    `,
    duration: '120 minutes',
    difficulty: 'advanced' as const,
    order: 1,
    estimatedTime: '120 minutes',
    objectives: [
      'Understand thread lifecycle and management',
      'Implement thread-safe code using synchronization',
      'Use ExecutorService for concurrent task execution',
      'Apply concurrent collection classes effectively'
    ],
    learningObjectives: [
      'Understand thread lifecycle and management',
      'Implement thread-safe code using synchronization',
      'Use ExecutorService for concurrent task execution',
      'Apply concurrent collection classes effectively'
    ],
    quiz: {
      questions: [
        {
          id: 'java-concurrency-1',
          question: 'Which is the preferred way to create and manage threads in modern Java applications?',
          options: [
            'Extending the Thread class directly',
            'Implementing Runnable and managing threads manually',
            'Using ExecutorService and thread pools',
            'Creating threads with lambda expressions only'
          ],
          correctAnswer: 2,
          correctAnswerIndex: 2,
          difficulty: 'medium' as const,
          explanation: 'ExecutorService with thread pools is the preferred approach as it provides better resource management, reusability, and control over thread lifecycle.',
          category: 'concurrency',
          points: 20,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-concurrency-2',
          question: 'What can happen if two threads access a shared variable without proper synchronization?',
          options: [
            'The program will compile but may produce incorrect results',
            'The program will not compile',
            'The threads will automatically synchronize',
            'Nothing, Java handles this automatically'
          ],
          correctAnswer: 0,
          correctAnswerIndex: 0,
          difficulty: 'hard' as const,
          explanation: 'Without proper synchronization, race conditions can occur where multiple threads access shared data simultaneously, leading to data corruption and unpredictable results.',
          category: 'thread-safety',
          points: 25,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 80,
      timeLimit: 600,
      category: 'java'
    }
  },
  {
    id: 'java-memory-jvm',
    title: 'Java Memory Management & JVM Internals',
    description: 'Deep dive into JVM architecture, memory management, garbage collection, and performance optimization.',
    content: `
# Java Memory Management & JVM Internals

Understanding how Java manages memory is crucial for building high-performance applications.

## JVM Architecture

### Memory Areas

\`\`\`java
// Heap Memory - Object storage
public class HeapExample {
    private String name;  // Stored in heap
    private int age;      // Primitive value in heap
    
    public void processData() {
        String local = "temporary";  // Reference in stack, object in heap
    }
}

// Stack Memory - Method calls and local variables
public void methodCall() {
    int x = 10;          // Primitive in stack
    int y = 20;          // Primitive in stack
    int result = x + y;  // Calculation in stack
}
\`\`\`

### Memory Structure
- **Heap**: Objects and instance variables
- **Stack**: Method calls, local variables, references
- **Metaspace**: Class metadata (replaced PermGen in Java 8+)
- **Code Cache**: Compiled native code

## Garbage Collection

### GC Algorithms

\`\`\`java
// Serial GC (single-threaded)
-XX:+UseSerialGC

// Parallel GC (multi-threaded)
-XX:+UseParallelGC

// CMS GC (low-latency)
-XX:+UseConcMarkSweepGC

// G1 GC (default from Java 9+)
-XX:+UseG1GC

// ZGC (ultra-low latency)
-XX:+UseZGC
\`\`\`

### Object Lifecycle
\`\`\`java
// Young Generation (Eden + Survivor spaces)
public void createObjects() {
    for (int i = 0; i < 1000; i++) {
        String temp = new String("Object " + i);
        // Most objects die young
    }
}

// Old Generation (Tenured)
public class LongLived {
    private static final List<String> cache = new ArrayList<>();
    // Long-lived objects promoted to old generation
}
\`\`\`

## Memory Leaks

### Common Causes
\`\`\`java
// 1. Static Collections
public class LeakExample {
    private static final List<Object> list = new ArrayList<>();
    
    public void addToCache(Object obj) {
        list.add(obj);  // Never removed!
    }
}

// 2. Unclosed Resources
public void processFile() {
    FileInputStream fis = new FileInputStream("file.txt");
    // If exception occurs, resource not closed
}

// 3. Inner Class References
public class Outer {
    private byte[] data = new byte[1000000];
    
    public class Inner {
        // Implicitly holds reference to Outer
    }
}
\`\`\`

### Prevention
\`\`\`java
// Use WeakReference for caches
Map<String, WeakReference<Object>> cache = new WeakHashMap<>();

// Always close resources
try (FileInputStream fis = new FileInputStream("file.txt")) {
    // Use file
}

// Use static inner classes when possible
public static class Inner {
    // No implicit outer reference
}
\`\`\`

## JVM Tuning

### Heap Size Configuration
\`\`\`bash
# Set initial heap size
-Xms512m

# Set maximum heap size
-Xmx4g

# Set young generation size
-Xmn1g

# Set metaspace size
-XX:MetaspaceSize=256m
-XX:MaxMetaspaceSize=512m
\`\`\`

### GC Logging
\`\`\`bash
# Enable GC logging
-Xlog:gc*:file=gc.log

# Print GC details
-XX:+PrintGCDetails
-XX:+PrintGCDateStamps

# GC overhead limit
-XX:GCTimeRatio=19
\`\`\`

## Performance Monitoring

\`\`\`java
// Using JMX for monitoring
import java.lang.management.*;

public class MemoryMonitor {
    public static void printMemoryInfo() {
        MemoryMXBean memoryBean = ManagementFactory.getMemoryMXBean();
        MemoryUsage heapUsage = memoryBean.getHeapMemoryUsage();
        
        System.out.println("Heap Memory Used: " + 
            heapUsage.getUsed() / (1024 * 1024) + " MB");
        System.out.println("Heap Memory Max: " + 
            heapUsage.getMax() / (1024 * 1024) + " MB");
    }
}
\`\`\`

## Best Practices

1. **Minimize object creation** in performance-critical code
2. **Use object pools** for expensive objects
3. **Avoid finalize()** method - use try-with-resources
4. **Monitor GC logs** regularly in production
5. **Profile before optimizing** - don't guess
6. **Use appropriate GC** for your application type
7. **Set heap size** explicitly for production apps
    `,
    duration: '120 minutes',
    difficulty: 'advanced' as const,
    order: 2,
    estimatedTime: '120 minutes',
    objectives: [
      'Understand JVM memory architecture',
      'Master garbage collection concepts and tuning',
      'Identify and prevent memory leaks',
      'Optimize application memory usage'
    ],
    learningObjectives: [
      'Understand JVM memory architecture',
      'Master garbage collection concepts and tuning',
      'Identify and prevent memory leaks',
      'Optimize application memory usage'
    ],
    quiz: {
      questions: [
        {
          id: 'java-memory-1',
          question: 'Where are primitive local variables stored in Java?',
          options: [
            'Heap memory',
            'Stack memory',
            'Metaspace',
            'Code cache'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'Primitive local variables are stored in stack memory along with method call information and object references.',
          category: 'memory',
          points: 20,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-memory-2',
          question: 'What is the main advantage of G1 GC over CMS GC?',
          options: [
            'G1 GC is faster',
            'G1 GC provides better predictability and lower pause times',
            'G1 GC uses less memory',
            'G1 GC is simpler to configure'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'hard' as const,
          explanation: 'G1 GC provides more predictable pause times through its region-based approach and can meet specified pause time goals.',
          category: 'gc',
          points: 25,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 80,
      timeLimit: 600,
      category: 'java'
    }
  },
  {
    id: 'java-design-patterns',
    title: 'Design Patterns in Java',
    description: 'Master essential design patterns including Creational, Structural, and Behavioral patterns.',
    content: `
# Design Patterns in Java

Design patterns are proven solutions to common software design problems.

## Creational Patterns

### Singleton Pattern
\`\`\`java
public class DatabaseConnection {
    private static volatile DatabaseConnection instance;
    private Connection connection;
    
    private DatabaseConnection() {
        // Initialize connection
    }
    
    public static DatabaseConnection getInstance() {
        if (instance == null) {
            synchronized (DatabaseConnection.class) {
                if (instance == null) {
                    instance = new DatabaseConnection();
                }
            }
        }
        return instance;
    }
}
\`\`\`

### Factory Pattern
\`\`\`java
public interface Animal {
    void makeSound();
}

public class Dog implements Animal {
    public void makeSound() { System.out.println("Woof!"); }
}

public class Cat implements Animal {
    public void makeSound() { System.out.println("Meow!"); }
}

public class AnimalFactory {
    public static Animal createAnimal(String type) {
        switch (type.toLowerCase()) {
            case "dog": return new Dog();
            case "cat": return new Cat();
            default: throw new IllegalArgumentException("Unknown animal type");
        }
    }
}
\`\`\`

### Builder Pattern
\`\`\`java
public class User {
    private final String firstName;
    private final String lastName;
    private final String email;
    private final String phone;
    
    private User(Builder builder) {
        this.firstName = builder.firstName;
        this.lastName = builder.lastName;
        this.email = builder.email;
        this.phone = builder.phone;
    }
    
    public static class Builder {
        private String firstName;
        private String lastName;
        private String email;
        private String phone;
        
        public Builder firstName(String firstName) {
            this.firstName = firstName;
            return this;
        }
        
        public Builder lastName(String lastName) {
            this.lastName = lastName;
            return this;
        }
        
        public Builder email(String email) {
            this.email = email;
            return this;
        }
        
        public Builder phone(String phone) {
            this.phone = phone;
            return this;
        }
        
        public User build() {
            return new User(this);
        }
    }
}

// Usage
User user = new User.Builder()
    .firstName("John")
    .lastName("Doe")
    .email("john@example.com")
    .build();
\`\`\`

## Structural Patterns

### Adapter Pattern
\`\`\`java
// Legacy interface
interface OldPrinter {
    void printOld(String text);
}

// New interface
interface ModernPrinter {
    void print(String text);
}

// Adapter
class PrinterAdapter implements ModernPrinter {
    private OldPrinter oldPrinter;
    
    public PrinterAdapter(OldPrinter oldPrinter) {
        this.oldPrinter = oldPrinter;
    }
    
    @Override
    public void print(String text) {
        oldPrinter.printOld(text);
    }
}
\`\`\`

### Decorator Pattern
\`\`\`java
interface Coffee {
    double cost();
    String description();
}

class SimpleCoffee implements Coffee {
    public double cost() { return 2.0; }
    public String description() { return "Simple coffee"; }
}

abstract class CoffeeDecorator implements Coffee {
    protected Coffee coffee;
    
    public CoffeeDecorator(Coffee coffee) {
        this.coffee = coffee;
    }
}

class MilkDecorator extends CoffeeDecorator {
    public MilkDecorator(Coffee coffee) {
        super(coffee);
    }
    
    public double cost() {
        return coffee.cost() + 0.5;
    }
    
    public String description() {
        return coffee.description() + ", milk";
    }
}

// Usage
Coffee coffee = new SimpleCoffee();
coffee = new MilkDecorator(coffee);
coffee = new SugarDecorator(coffee);
\`\`\`

## Behavioral Patterns

### Observer Pattern
\`\`\`java
interface Observer {
    void update(String message);
}

class Subject {
    private List<Observer> observers = new ArrayList<>();
    
    public void attach(Observer observer) {
        observers.add(observer);
    }
    
    public void notifyAllObservers(String message) {
        for (Observer observer : observers) {
            observer.update(message);
        }
    }
}

class ConcreteObserver implements Observer {
    private String name;
    
    public ConcreteObserver(String name) {
        this.name = name;
    }
    
    public void update(String message) {
        System.out.println(name + " received: " + message);
    }
}
\`\`\`

### Strategy Pattern
\`\`\`java
interface PaymentStrategy {
    void pay(double amount);
}

class CreditCardPayment implements PaymentStrategy {
    public void pay(double amount) {
        System.out.println("Paid " + amount + " using credit card");
    }
}

class PayPalPayment implements PaymentStrategy {
    public void pay(double amount) {
        System.out.println("Paid " + amount + " using PayPal");
    }
}

class ShoppingCart {
    private PaymentStrategy paymentStrategy;
    
    public void setPaymentStrategy(PaymentStrategy strategy) {
        this.paymentStrategy = strategy;
    }
    
    public void checkout(double amount) {
        paymentStrategy.pay(amount);
    }
}
\`\`\`

### Command Pattern
\`\`\`java
interface Command {
    void execute();
}

class Light {
    public void on() { System.out.println("Light is ON"); }
    public void off() { System.out.println("Light is OFF"); }
}

class LightOnCommand implements Command {
    private Light light;
    
    public LightOnCommand(Light light) {
        this.light = light;
    }
    
    public void execute() {
        light.on();
    }
}

class RemoteControl {
    private Command command;
    
    public void setCommand(Command command) {
        this.command = command;
    }
    
    public void pressButton() {
        command.execute();
    }
}
\`\`\`

## Best Practices

1. **Don't overuse patterns** - apply when needed
2. **Favor composition over inheritance**
3. **Program to interfaces**, not implementations
4. **Keep it simple** - don't over-engineer
5. **Consider SOLID principles** when applying patterns
6. **Document pattern usage** for maintainability
    `,
    duration: '90 minutes',
    difficulty: 'advanced' as const,
    order: 3,
    estimatedTime: '90 minutes',
    objectives: [
      'Understand and implement Creational patterns',
      'Apply Structural patterns for flexible design',
      'Use Behavioral patterns for better code organization',
      'Choose appropriate patterns for specific problems'
    ],
    learningObjectives: [
      'Understand and implement Creational patterns',
      'Apply Structural patterns for flexible design',
      'Use Behavioral patterns for better code organization',
      'Choose appropriate patterns for specific problems'
    ],
    quiz: {
      questions: [
        {
          id: 'java-patterns-1',
          question: 'Which pattern ensures a class has only one instance?',
          options: [
            'Factory Pattern',
            'Singleton Pattern',
            'Builder Pattern',
            'Prototype Pattern'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'easy' as const,
          explanation: 'The Singleton Pattern ensures only one instance of a class exists and provides global access to it.',
          category: 'design-patterns',
          points: 15,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-patterns-2',
          question: 'What is the main benefit of the Strategy Pattern?',
          options: [
            'It makes objects immutable',
            'It allows selecting algorithm at runtime',
            'It creates singleton instances',
            'It provides lazy initialization'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'The Strategy Pattern defines a family of algorithms and makes them interchangeable, allowing the algorithm to be selected at runtime.',
          category: 'design-patterns',
          points: 20,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 400,
      category: 'java'
    }
  },
  {
    id: 'java-reactive',
    title: 'Reactive Programming with Java',
    description: 'Learn reactive programming concepts using Project Reactor and reactive streams.',
    content: `
# Reactive Programming with Java

Reactive programming enables building asynchronous, event-driven applications that can handle high loads efficiently.

## Reactive Streams

### Core Concepts
\`\`\`java
// Publisher - produces data
Publisher<String> publisher = subscriber -> {
    subscriber.onNext("Hello");
    subscriber.onNext("World");
    subscriber.onComplete();
};

// Subscriber - consumes data
Subscriber<String> subscriber = new Subscriber<>() {
    public void onSubscribe(Subscription s) {
        s.request(Long.MAX_VALUE);
    }
    
    public void onNext(String item) {
        System.out.println("Received: " + item);
    }
    
    public void onError(Throwable t) {
        System.err.println("Error: " + t.getMessage());
    }
    
    public void onComplete() {
        System.out.println("Completed");
    }
};
\`\`\`

## Project Reactor

### Flux - Multiple Items
\`\`\`java
import reactor.core.publisher.Flux;

// Create Flux from items
Flux<String> flux = Flux.just("A", "B", "C");

// Create Flux from range
Flux<Integer> numbers = Flux.range(1, 5);

// Subscribe and process
flux.subscribe(
    item -> System.out.println("Item: " + item),
    error -> System.err.println("Error: " + error),
    () -> System.out.println("Complete")
);
\`\`\`

### Mono - Single Item
\`\`\`java
import reactor.core.publisher.Mono;

// Create Mono
Mono<String> mono = Mono.just("Hello");

// Empty Mono
Mono<String> empty = Mono.empty();

// Mono with delay
Mono<String> delayed = Mono.just("Delayed")
    .delayElement(Duration.ofSeconds(2));
\`\`\`

## Operators

### Transformation
\`\`\`java
// Map - transform items
Flux<Integer> doubled = Flux.range(1, 5)
    .map(i -> i * 2);

// FlatMap - transform to publishers
Flux<String> expanded = Flux.just("A", "B")
    .flatMap(s -> Flux.just(s + "1", s + "2"));

// Filter
Flux<Integer> evens = Flux.range(1, 10)
    .filter(i -> i % 2 == 0);
\`\`\`

### Combining
\`\`\`java
Flux<String> flux1 = Flux.just("A", "B");
Flux<String> flux2 = Flux.just("C", "D");

// Merge - interleave emissions
Flux<String> merged = Flux.merge(flux1, flux2);

// Concat - sequential
Flux<String> concatenated = Flux.concat(flux1, flux2);

// Zip - combine
Flux<String> zipped = Flux.zip(flux1, flux2, (a, b) -> a + b);
\`\`\`

### Error Handling
\`\`\`java
Flux<Integer> flux = Flux.range(1, 5)
    .map(i -> {
        if (i == 3) throw new RuntimeException("Error at 3");
        return i;
    })
    .onErrorReturn(-1)  // Return default value
    .onErrorResume(e -> Flux.just(10, 20))  // Switch to fallback
    .retry(3);  // Retry on error
\`\`\`

## Backpressure

\`\`\`java
// Handling backpressure
Flux.range(1, 1000)
    .onBackpressureBuffer(100)  // Buffer up to 100 items
    .onBackpressureDrop()        // Drop excess items
    .onBackpressureLatest()      // Keep only latest
    .subscribe(new BaseSubscriber<>() {
        @Override
        protected void hookOnSubscribe(Subscription subscription) {
            request(10);  // Request 10 items at a time
        }
        
        @Override
        protected void hookOnNext(Integer value) {
            System.out.println(value);
            request(1);  // Request one more
        }
    });
\`\`\`

## Schedulers

\`\`\`java
// Different execution contexts
Flux.range(1, 5)
    .publishOn(Schedulers.parallel())     // Compute-intensive
    .subscribeOn(Schedulers.boundedElastic())  // I/O operations
    .subscribe();

// Custom scheduler
Scheduler custom = Schedulers.newParallel("custom", 4);
\`\`\`

## Practical Example

\`\`\`java
public class ReactiveUserService {
    private final WebClient webClient;
    
    public Mono<User> getUser(String id) {
        return webClient.get()
            .uri("/users/{id}", id)
            .retrieve()
            .bodyToMono(User.class)
            .timeout(Duration.ofSeconds(5))
            .retry(3)
            .onErrorResume(e -> {
                log.error("Failed to get user", e);
                return Mono.empty();
            });
    }
    
    public Flux<User> getAllUsers() {
        return webClient.get()
            .uri("/users")
            .retrieve()
            .bodyToFlux(User.class)
            .filter(user -> user.isActive())
            .take(100);
    }
    
    public Mono<Void> saveUsers(List<User> users) {
        return Flux.fromIterable(users)
            .flatMap(this::saveUser)
            .then();
    }
}
\`\`\`

## Testing Reactive Code

\`\`\`java
@Test
public void testFlux() {
    Flux<String> flux = Flux.just("A", "B", "C");
    
    StepVerifier.create(flux)
        .expectNext("A")
        .expectNext("B")
        .expectNext("C")
        .verifyComplete();
}

@Test
public void testError() {
    Flux<Integer> flux = Flux.error(new RuntimeException("Error"));
    
    StepVerifier.create(flux)
        .expectError(RuntimeException.class)
        .verify();
}
\`\`\`

## Best Practices

1. **Don't block** in reactive pipelines
2. **Handle errors** at appropriate levels
3. **Use appropriate schedulers** for operations
4. **Test with StepVerifier**
5. **Manage backpressure** properly
6. **Avoid nested subscribe()** calls
7. **Use operators** instead of imperative logic
    `,
    duration: '105 minutes',
    difficulty: 'advanced' as const,
    order: 4,
    estimatedTime: '105 minutes',
    objectives: [
      'Understand reactive programming principles',
      'Use Flux and Mono for async operations',
      'Apply reactive operators effectively',
      'Handle backpressure and errors'
    ],
    learningObjectives: [
      'Understand reactive programming principles',
      'Use Flux and Mono for async operations',
      'Apply reactive operators effectively',
      'Handle backpressure and errors'
    ],
    quiz: {
      questions: [
        {
          id: 'java-reactive-1',
          question: 'What is the difference between Flux and Mono?',
          options: [
            'Flux is synchronous, Mono is asynchronous',
            'Flux emits 0-N items, Mono emits 0-1 item',
            'Flux is faster than Mono',
            'Mono can only emit errors'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'easy' as const,
          explanation: 'Flux represents a reactive stream of 0 to N items, while Mono represents a stream of 0 or 1 item.',
          category: 'reactive',
          points: 15,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-reactive-2',
          question: 'What is backpressure in reactive programming?',
          options: [
            'The pressure to write reactive code',
            'A mechanism to handle fast producers and slow consumers',
            'The time it takes to process data',
            'A type of error handling'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'Backpressure is a mechanism that allows consumers to signal producers to slow down when they cannot keep up with the data flow.',
          category: 'reactive',
          points: 20,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 500,
      category: 'java'
    }
  },
  {
    id: 'java-spring-boot',
    title: 'Microservices with Spring Boot',
    description: 'Build production-ready microservices using Spring Boot, REST APIs, and best practices.',
    content: `
# Microservices with Spring Boot

Spring Boot simplifies building production-grade Spring applications with minimal configuration.

## Getting Started

### Project Setup
\`\`\`java
@SpringBootApplication
public class Application {
    public static void main(String[] args) {
        SpringApplication.run(Application.class, args);
    }
}
\`\`\`

### Application Properties
\`\`\`properties
# application.properties
server.port=8080
spring.application.name=user-service

# Database
spring.datasource.url=jdbc:mysql://localhost:3306/mydb
spring.datasource.username=root
spring.datasource.password=password

# JPA
spring.jpa.hibernate.ddl-auto=update
spring.jpa.show-sql=true
\`\`\`

## REST API Development

### Controller
\`\`\`java
@RestController
@RequestMapping("/api/users")
public class UserController {
    
    @Autowired
    private UserService userService;
    
    @GetMapping
    public List<User> getAllUsers() {
        return userService.findAll();
    }
    
    @GetMapping("/{id}")
    public ResponseEntity<User> getUserById(@PathVariable Long id) {
        return userService.findById(id)
            .map(ResponseEntity::ok)
            .orElse(ResponseEntity.notFound().build());
    }
    
    @PostMapping
    public ResponseEntity<User> createUser(@Valid @RequestBody User user) {
        User saved = userService.save(user);
        return ResponseEntity.created(
            URI.create("/api/users/" + saved.getId())
        ).body(saved);
    }
    
    @PutMapping("/{id}")
    public ResponseEntity<User> updateUser(
            @PathVariable Long id, 
            @Valid @RequestBody User user) {
        return userService.update(id, user)
            .map(ResponseEntity::ok)
            .orElse(ResponseEntity.notFound().build());
    }
    
    @DeleteMapping("/{id}")
    public ResponseEntity<Void> deleteUser(@PathVariable Long id) {
        userService.deleteById(id);
        return ResponseEntity.noContent().build();
    }
}
\`\`\`

### Service Layer
\`\`\`java
@Service
public class UserService {
    
    @Autowired
    private UserRepository userRepository;
    
    public List<User> findAll() {
        return userRepository.findAll();
    }
    
    public Optional<User> findById(Long id) {
        return userRepository.findById(id);
    }
    
    @Transactional
    public User save(User user) {
        return userRepository.save(user);
    }
    
    @Transactional
    public Optional<User> update(Long id, User user) {
        return userRepository.findById(id)
            .map(existing -> {
                existing.setName(user.getName());
                existing.setEmail(user.getEmail());
                return userRepository.save(existing);
            });
    }
    
    @Transactional
    public void deleteById(Long id) {
        userRepository.deleteById(id);
    }
}
\`\`\`

### Repository
\`\`\`java
@Repository
public interface UserRepository extends JpaRepository<User, Long> {
    
    Optional<User> findByEmail(String email);
    
    List<User> findByNameContaining(String name);
    
    @Query("SELECT u FROM User u WHERE u.age > :age")
    List<User> findUsersOlderThan(@Param("age") int age);
}
\`\`\`

## Dependency Injection

\`\`\`java
// Field injection (not recommended)
@Autowired
private UserService userService;

// Constructor injection (recommended)
private final UserService userService;

@Autowired
public UserController(UserService userService) {
    this.userService = userService;
}

// Setter injection
private UserService userService;

@Autowired
public void setUserService(UserService userService) {
    this.userService = userService;
}
\`\`\`

## Exception Handling

\`\`\`java
@RestControllerAdvice
public class GlobalExceptionHandler {
    
    @ExceptionHandler(ResourceNotFoundException.class)
    public ResponseEntity<ErrorResponse> handleNotFound(
            ResourceNotFoundException ex) {
        ErrorResponse error = new ErrorResponse(
            HttpStatus.NOT_FOUND.value(),
            ex.getMessage(),
            LocalDateTime.now()
        );
        return ResponseEntity.status(HttpStatus.NOT_FOUND).body(error);
    }
    
    @ExceptionHandler(MethodArgumentNotValidException.class)
    public ResponseEntity<ErrorResponse> handleValidation(
            MethodArgumentNotValidException ex) {
        Map<String, String> errors = new HashMap<>();
        ex.getBindingResult().getFieldErrors().forEach(error ->
            errors.put(error.getField(), error.getDefaultMessage())
        );
        
        ErrorResponse error = new ErrorResponse(
            HttpStatus.BAD_REQUEST.value(),
            "Validation failed",
            errors,
            LocalDateTime.now()
        );
        return ResponseEntity.badRequest().body(error);
    }
}
\`\`\`

## Configuration

\`\`\`java
@Configuration
public class AppConfig {
    
    @Bean
    public RestTemplate restTemplate() {
        return new RestTemplate();
    }
    
    @Bean
    public ModelMapper modelMapper() {
        return new ModelMapper();
    }
    
    @Bean
    public PasswordEncoder passwordEncoder() {
        return new BCryptPasswordEncoder();
    }
}
\`\`\`

## Validation

\`\`\`java
@Entity
public class User {
    
    @Id
    @GeneratedValue(strategy = GenerationType.IDENTITY)
    private Long id;
    
    @NotBlank(message = "Name is required")
    @Size(min = 2, max = 100)
    private String name;
    
    @Email(message = "Invalid email format")
    @NotBlank(message = "Email is required")
    private String email;
    
    @Min(value = 18, message = "Age must be at least 18")
    @Max(value = 120, message = "Age must be less than 120")
    private Integer age;
    
    @Pattern(regexp = "^\\+?[1-9]\\d{1,14}$", 
             message = "Invalid phone number")
    private String phone;
}
\`\`\`

## Testing

\`\`\`java
@SpringBootTest
@AutoConfigureMockMvc
public class UserControllerTest {
    
    @Autowired
    private MockMvc mockMvc;
    
    @MockBean
    private UserService userService;
    
    @Test
    public void testGetUserById() throws Exception {
        User user = new User(1L, "John", "john@example.com");
        when(userService.findById(1L)).thenReturn(Optional.of(user));
        
        mockMvc.perform(get("/api/users/1"))
            .andExpect(status().isOk())
            .andExpect(jsonPath("$.name").value("John"))
            .andExpect(jsonPath("$.email").value("john@example.com"));
    }
    
    @Test
    public void testCreateUser() throws Exception {
        User user = new User(null, "Jane", "jane@example.com");
        User saved = new User(1L, "Jane", "jane@example.com");
        when(userService.save(any(User.class))).thenReturn(saved);
        
        mockMvc.perform(post("/api/users")
            .contentType(MediaType.APPLICATION_JSON)
            .content("{\"name\":\"Jane\",\"email\":\"jane@example.com\"}"))
            .andExpect(status().isCreated())
            .andExpect(header().exists("Location"));
    }
}
\`\`\`

## Microservices Architecture

### Service Communication
\`\`\`java
@Service
public class OrderService {
    
    @Autowired
    private RestTemplate restTemplate;
    
    public User getUserInfo(Long userId) {
        String url = "http://user-service/api/users/" + userId;
        return restTemplate.getForObject(url, User.class);
    }
}
\`\`\`

### Service Discovery (Eureka)
\`\`\`java
@SpringBootApplication
@EnableEurekaClient
public class UserServiceApplication {
    public static void main(String[] args) {
        SpringApplication.run(UserServiceApplication.class, args);
    }
}
\`\`\`

## Best Practices

1. **Use constructor injection** for required dependencies
2. **Implement proper exception handling** with @ControllerAdvice
3. **Validate input** using Bean Validation annotations
4. **Write tests** for controllers, services, and repositories
5. **Use DTOs** to separate API contract from domain model
6. **Implement proper logging** with SLF4J
7. **Externalize configuration** using application.properties/yml
8. **Use profiles** for different environments
    `,
    duration: '120 minutes',
    difficulty: 'advanced' as const,
    order: 5,
    estimatedTime: '120 minutes',
    objectives: [
      'Build RESTful APIs with Spring Boot',
      'Implement dependency injection and IoC',
      'Create layered architecture (Controller-Service-Repository)',
      'Apply microservices best practices'
    ],
    learningObjectives: [
      'Build RESTful APIs with Spring Boot',
      'Implement dependency injection and IoC',
      'Create layered architecture (Controller-Service-Repository)',
      'Apply microservices best practices'
    ],
    quiz: {
      questions: [
        {
          id: 'java-spring-1',
          question: 'What is the recommended way to inject dependencies in Spring Boot?',
          options: [
            'Field injection with @Autowired',
            'Setter injection',
            'Constructor injection',
            'Method injection'
          ],
          correctAnswer: 2,
          correctAnswerIndex: 2,
          difficulty: 'easy' as const,
          explanation: 'Constructor injection is recommended as it makes dependencies explicit, enables immutability, and facilitates testing.',
          category: 'spring-boot',
          points: 15,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-spring-2',
          question: 'What annotation is used to handle exceptions globally in Spring Boot?',
          options: [
            '@ExceptionHandler',
            '@ControllerAdvice',
            '@ResponseStatus',
            '@ErrorHandler'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: '@ControllerAdvice (or @RestControllerAdvice) allows handling exceptions globally across all controllers in the application.',
          category: 'spring-boot',
          points: 20,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 600,
      category: 'java'
    }
  }
];

export const JAVA_ADVANCED_COURSE: JavaCourse = {
  id: 'java-advanced',
  title: 'Advanced Java Programming',
  description: 'Master advanced Java concepts including concurrency, performance optimization, and enterprise patterns.',
  level: 'advanced',
  totalDuration: '18 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completion of Intermediate Java course',
    'Strong understanding of OOP and collections',
    'Experience with Java development tools'
  ],
  learningOutcomes: [
    'Build high-performance concurrent applications',
    'Implement advanced design patterns',
    'Optimize Java application performance',
    'Understand JVM internals and memory management'
  ],
  modules: JAVA_ADVANCED_MODULES,
  type: 'java'
};
